import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { getRiskBandsMaxLetter } from "@/features/branding";
import type { HazardPlusTotal } from "@/features/pe_aldd";
import { mvarToGradedRiskValue, RiskBand } from "@/features/risk_bands";
import { ADAPTATION_TERM_COLORS, adaptationConfig, adaptationTermsConfig, AdaptationType } from "@/types/adaptation";
import { formatAbbreviatedValueWithMetricUnit } from "@/utils/number";
import CloseOutlined from "@ant-design/icons/es/icons/CloseOutlined";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import styles from "./AdaptationPathwayComparison.module.scss";
import { DeltaValue } from "./charts/common/Text";
import { isHazardMetricDataAvailable, MetricKey, multiplierForMetric, unitForMetric } from "./charts/common/utils";
type MetricGroup = Partial<Record<MetricKey, number | null>>;
export type PathwayColumnProps = {
  id: string;
  pathwayId: string;
  adaptationType: AdaptationType;
} & MetricGroup;

// metric keys arr preserves order
const metricKeys: MetricKey[] = ["avg_damage_mtd", "avg_failure_mtd", "productivity_loss", "ttip"];
type PathwayInsightsRow = {
  key: MetricKey | "risk_rating";
  metric: React.ReactNode;
};

// Sub-columns revealed when a pathway is highlighted, in display order.
type DeltaColType = "absoluteChange" | "relativeChange";
const deltaColumnKeys: DeltaColType[] = ["absoluteChange", "relativeChange"];
const HIGHLIGHTED_SPAN = 1 + deltaColumnKeys.length;
const METRIC_COL_WIDTH = 142;
const BASELINE_COL_WIDTH = 90;
const AdaptationPathwayComparison = ({
  pathwayColumns,
  regionType,
  hazard,
  highLightedPathwayId,
  setHighLightedPathwayId = () => null
}: {
  pathwayColumns: PathwayColumnProps[];
  regionType?: string;
  hazard: HazardPlusTotal;
  highLightedPathwayId: string | null;
  setHighLightedPathwayId: (id: string | null) => void;
}) => {
  const {
    t
  } = useTranslation("terms");

  // Ratings are derived from MVAR, so they share its availability.
  const isRowAvailable = (key: PathwayInsightsRow["key"]) => isHazardMetricDataAvailable(hazard, key === "risk_rating" ? "avg_damage_mtd" : key);
  const metricLabels: Partial<Record<MetricKey, string>> = {
    avg_damage_mtd: "MVAR",
    avg_failure_mtd: "Failure Probability",
    productivity_loss: "Productivity Loss",
    ttip: "TIP"
  };
  const renderFormattedValue = (value: number, metric: MetricKey) => {
    const unit = unitForMetric(metric);
    return formatAbbreviatedValueWithMetricUnit(value, unit);
  };

  // The comparison anchor for the delta columns.
  const baselineColumn = pathwayColumns.find(col => col.adaptationType === "baseline");
  const getDisplayValue = (col: PathwayColumnProps, metric: MetricKey) => (col[metric as keyof PathwayColumnProps] as number) * multiplierForMetric(metric);
  const renderValueCell = (col: PathwayColumnProps, record: PathwayInsightsRow) => {
    const available = isRowAvailable(record.key);
    if (record.key === "risk_rating") {
      const grade = mvarToGradedRiskValue(col.avg_damage_mtd ?? 0, getRiskBandsMaxLetter(regionType));
      return <RiskBand rating={grade} size={24} notApplicable={!available} />;
    }
    if (!available) return <span className="text-gray-400">N/A</span>;
    const metric = record.key as MetricKey;
    return renderFormattedValue(getDisplayValue(col, metric), metric);
  };
  const renderDeltaCell = (col: PathwayColumnProps, record: PathwayInsightsRow, kind: "absoluteChange" | "relativeChange") => {
    if (record.key === "risk_rating" || !baselineColumn) return "-";
    if (!isRowAvailable(record.key)) return <span className="text-gray-400">-</span>;
    const metric = record.key as MetricKey;
    return <span className="inline italic tabular-nums *:inline" style={{
      color: adaptationTermsConfig[kind].color
    }} data-sentry-component="renderDeltaCell" data-sentry-source-file="AdaptationPathwayComparison.tsx">
        <DeltaValue mode={kind} metric={metric} baseline={getDisplayValue(baselineColumn, metric)} compare={getDisplayValue(col, metric)} data-sentry-element="DeltaValue" data-sentry-source-file="AdaptationPathwayComparison.tsx" />
      </span>;
  };
  const dataSource: PathwayInsightsRow[] = [{
    key: "risk_rating",
    metric: <ContextualInfoButtonWithText id="simpleRiskRatings">{t("simpleRiskRatings")}</ContextualInfoButtonWithText>
  }, ...metricKeys.map(key => ({
    key,
    metric: <ContextualInfoButtonWithText id="metrics"> {metricLabels[key] ?? key}</ContextualInfoButtonWithText>
  }))];
  const deltaHeader: Record<DeltaColType, React.ReactNode> = {
    absoluteChange: <ContextualInfoButtonWithText rootClassName="text-xs" id={adaptationTermsConfig.absoluteChange.contextId}>
        <span style={{
        color: adaptationTermsConfig.absoluteChange.color
      }}>{t("absoluteChange")}</span>
      </ContextualInfoButtonWithText>,
    relativeChange: <ContextualInfoButtonWithText rootClassName="text-xs" className="text-xs" id={adaptationTermsConfig.relativeChange.contextId}>
        <span style={{
        color: adaptationTermsConfig.relativeChange.color
      }}>{t("relativeChange")}</span>
      </ContextualInfoButtonWithText>
  };

  // When a pathway is highlighted, hide every other pathway; baseline always stays.
  const visibleColumns = pathwayColumns.filter(col => col.adaptationType === "baseline" || highLightedPathwayId === null || highLightedPathwayId === col.pathwayId);

  // One header row, then a row per metric.
  const totalRows = 1 + dataSource.length;

  // Metric occupies track 1; each pathway takes 1 track, or HIGHLIGHTED_SPAN when highlighted.
  let nextTrack = 2;
  const placedColumns = visibleColumns.map(col => {
    const isBaseline = col.adaptationType === "baseline";
    const isHighlighted = !isBaseline && highLightedPathwayId === col.pathwayId;
    const span = isHighlighted ? HIGHLIGHTED_SPAN : 1;
    const startTrack = nextTrack;
    nextTrack += span;
    return {
      col,
      isBaseline,
      isHighlighted,
      startTrack,
      span
    };
  });
  const gridTemplateColumns = [`${METRIC_COL_WIDTH}px`, ...placedColumns.map(({
    isBaseline,
    isHighlighted
  }) => {
    if (isHighlighted) return `repeat(${HIGHLIGHTED_SPAN}, minmax(80px, 1fr))`;
    if (isBaseline && highLightedPathwayId) return `${BASELINE_COL_WIDTH}px`;
    return "minmax(90px, 1fr)";
  })].join(" ");

  // Shared by highlighted and unhighlighted pathways so their columns stay identical.
  const renderColumnBody = (col: PathwayColumnProps) => <>
      <div role="columnheader" className={`${styles.cell} ${styles.headerCell}`}>
        <div className="flex flex-1 items-center gap-1 text-xs">
          <ContextualInfoButtonWithText className="*:!text-xs" id={adaptationConfig[col.adaptationType]?.contextId ?? col.adaptationType} data-sentry-element="ContextualInfoButtonWithText" data-sentry-source-file="AdaptationPathwayComparison.tsx">
            <span style={{
            color: ADAPTATION_TERM_COLORS[col.adaptationType]
          }}>
              {t(`adaptationTypes.${col.adaptationType}`)}
            </span>
          </ContextualInfoButtonWithText>
        </div>
      </div>
      {dataSource.map(row => <div key={row.key} role="cell" className={styles.cell}>
          {renderValueCell(col, row)}
        </div>)}
    </>;
  return <div className={`${styles.wrapper} w-full`} data-sentry-component="AdaptationPathwayComparison" data-sentry-source-file="AdaptationPathwayComparison.tsx">
      <div role="table" aria-label={`${t("resilience")} pathway comparison`} aria-rowcount={totalRows} aria-colcount={nextTrack - 1} className={styles.grid} style={{
      gridTemplateColumns,
      gridTemplateRows: `repeat(${totalRows}, auto)`
    }}>
        {/* Metric label column (sticky) */}
        <div className={`${styles.column} ${styles.stickyLeft}`} style={{
        gridColumn: 1,
        left: 0
      }} role="presentation">
          <div role="columnheader" className={`${styles.cell} ${styles.headerCell} text-xs`}>
            Metric
          </div>
          {dataSource.map(row => <div key={row.key} role="rowheader" className={`text-xs ${styles.cell} ${isRowAvailable(row.key) ? "" : "text-gray-400"}`}>
              {row.metric}
            </div>)}
        </div>

        {placedColumns.map(({
        col,
        isBaseline,
        isHighlighted,
        startTrack,
        span
      }) => {
        const handleColumnClick = isBaseline ? undefined : () => setHighLightedPathwayId(isHighlighted ? null : col.pathwayId);
        if (isHighlighted) {
          return <div key={col.id} className={`${styles.highlightedGroup} rounded-md border border-transparent`} style={{
            gridColumn: `${startTrack} / span ${span}`,
            borderColor: "var(--color-accent)"
          }} onClick={handleColumnClick} role="presentation">
                <div className={styles.subColumn} style={{
              gridColumn: 1,
              gridRow: "1 / -1"
            }} role="presentation">
                  {renderColumnBody(col)}
                </div>
                {deltaColumnKeys.map((kind, i) => {
              // The last header also carries the close affordance.
              const isLast = i === deltaColumnKeys.length - 1;
              return <div key={kind} className={styles.subColumn} style={{
                gridColumn: i + 2,
                gridRow: "1 / -1"
              }} role="presentation">
                      <div role="columnheader" className={`${styles.cell} ${styles.headerCell} ${isLast ? "justify-between gap-2" : ""}`}>
                        {deltaHeader[kind]}
                        {isLast && <Tooltip title="Unselect Pathway">
                            <CloseOutlined style={{
                      color: "var(--color-accent)"
                    }} className="shrink-0 rounded-md p-1 hover:bg-gray-200" />
                          </Tooltip>}
                      </div>
                      {dataSource.map(row => <div key={row.key} role="cell" className={styles.cell}>
                          {renderDeltaCell(col, row, kind)}
                        </div>)}
                    </div>;
            })}
              </div>;
        }
        return <div key={col.id} className={`${styles.column} ${isBaseline ? styles.stickyLeft : styles.pathwayColumn}`} style={{
          gridColumn: startTrack,
          left: isBaseline ? METRIC_COL_WIDTH : undefined
        }} onClick={handleColumnClick} role="presentation">
              {renderColumnBody(col)}
            </div>;
      })}
      </div>
    </div>;
};
export default AdaptationPathwayComparison;